import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Music, Heart, ArrowLeft } from 'lucide-react';
import { fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';
import OfferModal from './offermodal';
import { IoIosArrowBack } from 'react-icons/io';

const InsufficientCoinsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [mainPack, setMainPack] = useState(null);
  const [offerPack, setOfferPack] = useState(null);
  const [activities, setActivities] = useState([]);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);

  // Get the data passed from the previous page
  const { song, cost, priceData, shoutOutDetails, fastTrack, walletCoins } =
    location.state || {};
  const [price, setPrice] = useState(priceData);

  const PROFILE_IMAGES = [
    '/images/profile_pic1.jpg',
    '/images/profile_pic2.jpg',
    '/images/profile_pic3.jpg',
    '/images/profile_pic4.jpg',
    '/images/profile_pic5.jpg',
    '/images/profile_pic6.jpg',
    '/images/profile_pic7.jpg',
    '/images/profile_pic8.jpg',
    '/images/profile_pic9.jpg',
  ];
  // Activities data
  const COLOR_PALETTE = [
    '#FF6B9D', // Light Pink
    '#4ECDC4', // Turquoise
    '#45B7D1', // Sky Blue
    '#96CEB4', // Mint Green
    '#FFEAA7', // Light Yellow
    '#DDA0DD', // Plum
    '#98D8C8', // Seafoam
    '#F7DC6F', // Banana Yellow
    '#BB8FCE', // Lavender
    '#85C1E9', // Light Blue
    '#F8C471', // Peach
    '#82E0AA', // Light Green
  ];

  // ✅ Add shuffle function
  function shuffle(arr) {
    // Fisher-Yates shuffle
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  useEffect(() => {
    if (!price) return;
    const visiblePacks = price.filter(pack => !pack.isHidden);
    const popularPack = visiblePacks.find(pack => pack.isPopular);
    setSelectedPackId(popularPack?.id || visiblePacks[0]?.id || null);
  }, []);

  const parseActivity = (feedText, index) => {
    if (typeof feedText !== 'string' || !feedText.trim()) return null;

    const match = feedText.match(/^(.{1,2})\s+(.+)$/);
    if (!match) return null;

    const emoji = match[1];
    const text = match[2];
    const firstWord = text.split(' ')[0];

    // Generate 2 profiles with different variations
    const profiles = generateProfiles(firstWord);

    return {
      id: Math.random(),
      emoji,
      text: feedText,
      profiles, // Now returns array of 2 profiles
    };
  };

  // New function to generate 2 profiles with variations
  const generateProfiles = firstWord => {
    const variations = ['both_text', 'both_image', 'text_image', 'image_text'];
    const selectedVariation =
      variations[Math.floor(Math.random() * variations.length)];

    const getRandomColor = () =>
      COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)];
    const getRandomImage = () =>
      PROFILE_IMAGES[Math.floor(Math.random() * PROFILE_IMAGES.length)];
    const getRandomLetter = () =>
      String.fromCharCode(65 + Math.floor(Math.random() * 26)); // A-Z

    switch (selectedVariation) {
      case 'both_text':
        return [
          {
            type: 'text',
            initial: firstWord[0]?.toUpperCase() || 'U',
            color: getRandomColor(),
          },
          {
            type: 'text',
            initial: getRandomLetter(),
            color: getRandomColor(),
          },
        ];

      case 'both_image':
        return [
          {
            type: 'image',
            image: getRandomImage(),
          },
          {
            type: 'image',
            image: getRandomImage(),
          },
        ];

      case 'text_image':
        return [
          {
            type: 'text',
            initial: firstWord[0]?.toUpperCase() || 'U',
            color: getRandomColor(),
          },
          {
            type: 'image',
            image: getRandomImage(),
          },
        ];

      case 'image_text':
        return [
          {
            type: 'image',
            image: getRandomImage(),
          },
          {
            type: 'text',
            initial: firstWord[0]?.toUpperCase() || 'U',
            color: getRandomColor(),
          },
        ];

      default:
        return [
          {
            type: 'text',
            initial: firstWord[0]?.toUpperCase() || 'U',
            color: getRandomColor(),
          },
          {
            type: 'text',
            initial: getRandomLetter(),
            color: getRandomColor(),
          },
        ];
    }
  };
  useEffect(() => {
    const fetchLiveFeed = async () => {
      try {
        const response = await fetchWithAuth(API_ENDPOINTS.LIVE_FEED);
        if (!response.ok) throw new Error('Failed to fetch live feed');
        const feedData = await response.json();
        const parsedActivities = feedData.map(parseActivity).filter(Boolean);

        // 🟢 SHUFFLE before setting activities!
        setActivities(shuffle(parsedActivities));

        // Optionally start with a random activity
        if (parsedActivities.length > 0) {
          setCurrentActivityIndex(
            Math.floor(Math.random() * parsedActivities.length)
          );
        }
      } catch (e) {
        console.error('Error fetching live feed:', e);
        setActivities([]);
      }
    };
    fetchLiveFeed();
  }, []);

  const currentActivity =
    activities.length > 0 ? activities[currentActivityIndex] : null;

  // Activity rotation effect
  useEffect(() => {
    if (activities.length === 0) return;

    const interval = setInterval(() => {
      setCurrentActivityIndex(prev => (prev + 1) % activities.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [activities.length]);

  // Load Razorpay script
  const loadRazorpay = async () => {
    return new Promise(resolve => {
      if (window.Razorpay) return resolve(true);

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const uploadShoutoutImage = async (shoutoutId, base64Image) => {
    // Convert base64 to blob
    const response = await fetch(base64Image);
    const blob = await response.blob();
    const file = new File([blob], 'shoutout-image.jpg', { type: 'image/jpeg' });

    const formData = new FormData();
    formData.append('picture', file);
    const token = localStorage.getItem('tringAuthToken');

    try {
      const uploadResponse = await fetch(
        API_ENDPOINTS.SHOUTOUT_PICTURE_UPLOAD(shoutoutId),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!uploadResponse.ok) throw new Error('Image upload failed');

      const data = await uploadResponse.json();
      console.log('Image upload response:', data);
      return data;
    } catch (error) {
      console.error('Image upload error:', error);
      throw error;
    }
  };

  // Fetch price function
  const fetchPrice = async () => {
    if (!price) return;
    try {
      const response = await fetchWithAuth(
        API_ENDPOINTS.ORDER_PRICE_BY_VENUE(localStorage.getItem('vId'))
      );
      if (!response.ok) throw new Error('Failed to fetch price');

      const data = await response.json();
      // Filter out hidden packs
      const visiblePacks = data.filter(pack => !pack.isHidden);
      setPrice(visiblePacks);

      // Set initial selected pack after fetching
      const popularPack = visiblePacks.find(pack => pack.isPopular);
      setSelectedPackId(popularPack?.id || visiblePacks[0]?.id || null);
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.ORDER_CONFIG);
      if (!response.ok) throw new Error('Failed to fetch config');

      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadRazorpay();

      // Check if priceData is available, if not fetch it
      if (priceData && priceData.length > 0) {
        setPrice(priceData);
        const popularPack = priceData.find(pack => pack.isPopular);
        setSelectedPackId(popularPack?.id || priceData[0]?.id || null);
      } else {
        // Fetch price data if not provided
        await fetchPrice();
      }

      await fetchConfig();

      setShowOfferModal(false);
      setMainPack(null);
      setOfferPack(null);
    };
    init();
  }, []);

  const handlePayment = (orderData, amount) => {
    if (!window.Razorpay || !config) {
      setIsLoading(false);
      return;
    }

    const options = {
      key: config.key_id,
      amount: amount,
      currency: 'INR',
      name: 'Tringbox',
      description: 'Coin Transaction',
      order_id: orderData.order_id,
      handler: async response => {
        setIsLoading(false);

        // Upload photo if this was a shoutout with a pending image
        const pendingShoutOutId = localStorage.getItem('pendingShoutOutId');
        const shoutOutImage = localStorage.getItem('shoutOutImage');

        if (pendingShoutOutId && shoutOutImage) {
          try {
            await uploadShoutoutImage(pendingShoutOutId, shoutOutImage);
            // Clean up after successful upload
            localStorage.removeItem('pendingShoutOutId');
            localStorage.removeItem('shoutOutImage');
          } catch (error) {
            console.error('Failed to upload shoutout image:', error);
            // Still proceed even if image upload fails
          }
        }

        localStorage.setItem('confetti', true);
        localStorage.setItem('shoutOutOpen', false);
        const currentVenueId = localStorage.getItem('vId');
        navigate(`/venues/${currentVenueId}`);
      },
      prefill: {
        contact: localStorage.getItem('number') || '',
      },
      theme: {
        color: '#CC151E',
      },
      method: {
        upi: true,
        card: false,
        netbanking: false,
        wallet: false,
        emi: false,
        paylater: false,
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', () => {
      setIsLoading(false);
      // Clean up on failure
      localStorage.removeItem('pendingShoutOutId');
      alert('Payment failed! Please try again.');
    });
    rzp.open();
  };
  useEffect(() => {
    const resetOnBack = () => {
      setIsLoading(false);
    };

    window.addEventListener('focus', resetOnBack);
    return () => {
      window.removeEventListener('focus', resetOnBack);
    };
  }, []);

  const makeOrder = async (overridePriceId = null) => {
    setIsLoading(true);
    const token = localStorage.getItem('tringAuthToken');
    const effectivePriceId = overridePriceId || selectedPackId;
    const priceArray = price || priceData || [];

    if (!token || !effectivePriceId || !priceArray.length) {
      setIsLoading(false);
      return;
    }

    try {
      const orderData = {
        priceId: effectivePriceId,
      };

      if (song?.id) {
        orderData.trackId = song.id;
        orderData.venueId = localStorage.getItem('vId');
      } else if (shoutOutDetails) {
        orderData.shoutOutMessage = `${shoutOutDetails.occasion} ${shoutOutDetails.name}`;
        orderData.wish = shoutOutDetails.occasion;
        orderData.recipientName = shoutOutDetails.name;
        orderData.additionalInfo = shoutOutDetails.desc || '';
        orderData.venueId = localStorage.getItem('vId');
        orderData.isMidnightShoutOut =
          shoutOutDetails.isMidnightShoutOut || false;
        if (shoutOutDetails.picture) {
          orderData.picture = shoutOutDetails.picture;
        }
      }

      if (fastTrack) {
        orderData.fastTrack = true;
      }

      const response = await fetchWithAuth(API_ENDPOINTS.ORDER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) throw new Error('Failed to create order');

      const data = await response.json();
      const selectedPack =
        priceArray.find(p => p.id === effectivePriceId) ||
        (offerPack && offerPack.id === effectivePriceId ? offerPack : null) ||
        (mainPack && mainPack.id === effectivePriceId ? mainPack : null);

      // If this is a shoutout order, store the shoutout ID for photo upload
      if (shoutOutDetails && data.shoutOutId) {
        localStorage.setItem('pendingShoutOutId', data.shoutOutId);
      }

      handlePayment(data, selectedPack?.price);
    } catch (error) {
      console.error('Error creating order:', error);
      setIsLoading(false);
    }
  };

  const handleBuyNow = async () => {
    const priceArray = price || priceData || [];
    if (!priceArray.length || !config) {
      alert('Payment options are still loading');
      return;
    }
    await makeOrder();
  };

  const handleClose = () => {
    navigate(-1);
  };

  // Helper function to get artist names
  const getArtistNames = artists => {
    if (!artists || !Array.isArray(artists) || artists.length === 0) {
      return 'Unknown Artist';
    }
    return artists.map(artist => artist.name).join(', ');
  };

  const priceArray = price || priceData || [];

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0%, 100% { transform: translateX(-100%) translateY(-100%) rotate(0deg); }
            50% { transform: translateX(0%) translateY(0%) rotate(180deg); }
          }
          
          @keyframes pulse-glow {
            0%, 100% {
              box-shadow: 0 6px 20px rgba(220, 38, 38, 0.3);
              transform: translateY(0);
            }
            50% {
              box-shadow: 0 8px 25px rgba(220, 38, 38, 0.5);
              transform: translateY(-1px);
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: .5;
            }
          }

          .animate-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }

          /* Prevent body scroll */
          body {
            overflow: hidden;
          }
        `}
      </style>

      <OfferModal
        show={showOfferModal}
        onClose={() => {
          setShowOfferModal(false);
          navigate(-1);
        }}
        mainPack={mainPack}
        offerPack={offerPack}
        makeOrder={makeOrder}
      />

      <div
        style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#FFFFFF',
          fontFamily:
            'Quicksand, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          overflow: 'hidden',
          overscrollBehaviorY: 'contain',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Header Section - Fixed Height */}
        <div
          style={{
            background: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: '16px 24px 8px 24px',
            flexShrink: 0,
            flexBasis: '60px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <IoIosArrowBack
              onClick={handleClose}
              size={25}
              style={{ color: '#CC151E' }}
            />

            <h1
              style={{
                flex: 1,
                textAlign: 'center',
                fontSize: '20px',
                fontWeight: '700',
                color: '#CC151E',
                margin: '0 16px',
                letterSpacing: '-0.025em',
              }}
            >
              Confirm & Play
            </h1>
            <div style={{ width: '44px' }}></div>
          </div>
        </div>

        {/* Activities Section - Fixed Height */}
        <div
          style={{
            padding: '12px 24px',
            backgroundColor: 'white',
            flexShrink: 0,
            flexBasis: '44px',
            marginBottom: '10px',
          }}
        >
          {currentActivity ? (
            <div
              style={{
                background: `linear-gradient(to right, ${
                  currentActivity.profiles[0].type === 'text'
                    ? currentActivity.profiles[0].color + '15'
                    : '#e5e5e5'
                }, ${
                  currentActivity.profiles[1].type === 'text'
                    ? currentActivity.profiles[1].color + '25'
                    : '#f0f0f0'
                })`,
                borderRadius: '16px',
                padding: '16px 20px',
                border: '0.7px solid #808080',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                transition: 'all 0.5s ease-in-out',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {/* Left: Profile Icon */}
              <div
                style={{
                  display: 'flex',
                  marginLeft: '-5px',
                }}
              >
                {currentActivity.profiles.map((profile, index) => (
                  <div
                    key={index}
                    style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor:
                        profile.type === 'text' ? profile.color : 'transparent',
                      border: '2px solid #FFFFFF',
                      color: '#FFFFFF',
                      fontSize: '12px',
                      fontWeight: '700',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                      zIndex: currentActivity.profiles.length - index,
                      marginLeft: index > 0 ? '-8px' : '0',
                      transition: 'all 0.5s',
                      overflow: 'hidden',
                    }}
                  >
                    {profile.type === 'text' ? (
                      profile.initial
                    ) : (
                      <img
                        src={profile.image}
                        alt="Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '50%',
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Middle: Full text (emoji + activity text together) */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontWeight: '500',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                  }}
                >
                  {currentActivity.text}
                </span>
              </div>

              {/* Right: Animated bars */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  marginLeft: '12px',
                  flexShrink: 0,
                }}
              >
                {[...Array(6)].map((_, index) => (
                  <div
                    key={index}
                    className="animate-pulse"
                    style={{
                      width: '2px',
                      borderRadius: '1px',
                      background: 'linear-gradient(to top, #CC151E, #b91c1c)',
                      height: `${8 + Math.sin(index * 0.8) * 4 + (index % 3) * 3}px`,
                      animationDelay: `${index * 0.15}s`,
                      animationDuration: `${1.2 + (index % 2) * 0.3}s`,
                      boxShadow: '0 1px 3px rgba(220, 38, 38, 0.3)',
                    }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                background: '#f5f5f5',
                borderRadius: '16px',
                padding: '16px 20px',
                border: '1px solid #e5e5e5',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <p
                style={{
                  margin: 0,
                  color: '#808080',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Loading live activities...
              </p>
            </div>
          )}
        </div>

        {/* Main Content - fills remaining space */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: '#F5F5F5',
            borderRadius: '24px 24px 0 0',
            boxShadow: 'inset 0 4px 6px rgba(0,0,0,0.05)',
            minHeight: 0,
          }}
        >
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              minHeight: 0,
            }}
          >
            {/* Content with padding */}
            <div
              style={{
                padding: '16px 24px 0px 24px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Your Request Section */}
              <div style={{ marginBottom: '14px' }}>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    textAlign: 'left',
                    marginBottom: '16px',
                  }}
                >
                  Your Request
                </h3>
                <div
                  style={{
                    border: '2px solid #e5e7eb',
                    borderRadius: '16px',
                    padding: '4px 16px',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {/* Song/Shoutout Icon */}
                    {song && song.album?.images?.[0]?.url ? (
                      <img
                        src={song.album.images[0].url}
                        alt="Album Art"
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '10px',
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <img
                        src={
                          localStorage.getItem('selectedThumbnail') ||
                          'https://images.tringbox.com/events/hb.png'
                        }
                        alt="Album Art"
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '10px',
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                      />
                    )}

                    {/* Request Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#111827',
                          margin: '0 0 4px 0',
                          textAlign: 'left',
                          lineHeight: '1.2',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                        }}
                      >
                        {song
                          ? song.name || 'Song Request'
                          : shoutOutDetails?.occasion || 'Shoutout Request'}
                      </h4>
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#6b7280',
                          margin: 0,
                          textAlign: 'left',
                          lineHeight: '1.3',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                        }}
                      >
                        {song
                          ? getArtistNames(song.artists)
                          : shoutOutDetails?.name || 'Someone'}
                      </p>
                    </div>
                  </div>

                  {/* Price (fixed at right) */}
                  <div
                    style={{
                      padding: '8px 8px',
                      borderRadius: '12px',
                      flexShrink: 0,
                      marginLeft: '16px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: 'black',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <img
                        src="/images/coin.svg"
                        alt="coin"
                        style={{
                          width: 35,
                          height: 35,
                        }}
                      />
                      {cost}
                    </span>
                  </div>
                </div>
              </div>

              {/* Select Coin Package Heading */}
              <div style={{ marginBottom: '16px' }}>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0,
                    textAlign: 'left',
                  }}
                >
                  Select Coin Package
                </h3>
              </div>

              {/* Loading state or Coin Packages */}
              {priceArray.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '40px',
                    color: '#6b7280',
                    flex: 1,
                  }}
                >
                  Loading coin packages...
                </div>
              ) : (
                <div style={{ flex: 1 }}>
                  {priceArray
                    .filter(pack => pack.coins >= cost - walletCoins) // ✅ Only show packs with enough coins
                    .map(pack => (
                      <div
                        key={pack.id}
                        onClick={async () => {
                          if (pack.offerPriceId) {
                            try {
                              const response = await fetchWithAuth(
                                API_ENDPOINTS.ORDER_PRICE_BY_ID(
                                  pack.offerPriceId
                                )
                              );
                              if (!response.ok) {
                                throw new Error('Failed to fetch offer pack');
                              }
                              const foundOfferPack = await response.json();
                              if (foundOfferPack) {
                                setMainPack(pack);
                                setOfferPack(foundOfferPack);
                                setShowOfferModal(true);
                                return;
                              }
                            } catch (err) {
                              console.error('Error fetching offer pack:', err);
                            }
                          }
                          setSelectedPackId(pack.id);
                        }}
                        style={{
                          position: 'relative',
                          border: `2px solid ${selectedPackId === pack.id ? '#CC151E' : '#e5e7eb'}`,
                          borderRadius: '16px',
                          padding: '14px',
                          marginBottom: '12px',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          background:
                            selectedPackId === pack.id
                              ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                              : 'white',
                          transform: 'translateY(0)',
                          boxShadow:
                            selectedPackId === pack.id
                              ? '0 8px 25px rgba(220, 38, 38, 0.2)'
                              : '0 2px 4px rgba(0,0,0,0.05)',
                        }}
                      >
                        {pack.isPopular && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '-10px',
                              left: '20px',
                              background:
                                'linear-gradient(135deg, #CC151E 0%, #b91c1c 100%)',
                              color: 'white',
                              padding: '6px 16px',
                              borderRadius: '20px',
                              fontSize: '7px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Most Popular
                          </div>
                        )}

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                            }}
                          >
                            {/* Radio Input */}
                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                border: `2px solid ${selectedPackId === pack.id ? '#CC151E' : '#d1d5db'}`,
                                borderRadius: '50%',
                                position: 'relative',
                                background:
                                  selectedPackId === pack.id
                                    ? '#CC151E'
                                    : 'transparent',
                              }}
                            >
                              {selectedPackId === pack.id && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '8px',
                                    height: '8px',
                                    background: 'white',
                                    borderRadius: '50%',
                                  }}
                                />
                              )}
                            </div>

                            {/* Coin Icon */}
                            <img
                              src="/images/coin.svg"
                              alt="coin"
                              style={{
                                width: 37,
                                height: 37,
                                marginBottom: 4,
                              }}
                            />

                            {/* Package Info */}
                            <div>
                              <h3
                                style={{
                                  fontSize: '17px',
                                  fontWeight: '600',
                                  color: '#111827',
                                  marginBottom: '4px',
                                  margin: 0,
                                  textAlign: 'left',
                                }}
                              >
                                {pack.coins} Coins
                              </h3>
                              <p
                                style={{
                                  fontSize: '12px',
                                  color: '#6b7280',
                                  margin: 0,
                                  textAlign: 'left',
                                }}
                              >
                                Upto {pack.songs} Request
                                {pack.songs > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>

                          {/* Package Price */}
                          <div style={{ textAlign: 'right' }}>
                            <div
                              style={{
                                fontSize: '21px',
                                fontWeight: '700',
                                color: '#111827',
                                marginBottom: '4px',
                              }}
                            >
                              ₹{pack.price}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Buy Button - Sticky at bottom */}
            <div style={{ padding: '6px 24px 24px 24px', flexShrink: 0 }}>
              <button
                onClick={handleBuyNow}
                disabled={isLoading || priceArray.length === 0}
                style={{
                  background:
                    'linear-gradient(90deg, #C81620 -13.89%, #8A325D 93.25%)',
                  color: 'white',
                  border: 'none',
                  padding: '16px',
                  borderRadius: '16px',
                  fontSize: '17px',
                  fontWeight: '600',
                  cursor:
                    isLoading || priceArray.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 6px 20px rgba(220, 38, 38, 0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <span>Continue</span>
                  {!isLoading && (
                    <span
                      style={{
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      →
                    </span>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InsufficientCoinsPage;
